import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  FileText,
  Clock,
  ArrowLeft,
  User,
  Calendar,
  Target,
  BookOpen,
  Download,
  Shield
} from 'lucide-react';
import { getUser, authenticatedFetch } from '@/lib/auth';
import { formatDateTime } from '@/utils/date';
import { formatStatus, getStatusColor } from '@/utils/format';
import type { StudyPlan, User as UserType } from '@/types';
import { VersionHistory } from './VersionHistory';
import { VersionCompare } from './VersionCompare';
import { generateStudyPlanWord } from '@/utils/word';
import { useToast } from './ui';

export default function StudyPlanReview() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const [plan, setPlan] = useState<StudyPlan | null>(null);
  const [currentUser, setCurrentUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  // Task #348 (2026-07-07): fire-sone terskler fra DB
  const [thresholds, setThresholds] = useState({
    auto_approve_threshold: 80,
    self_approve_with_justification_threshold: 70,
    manual_review_threshold: 60,
    minimum_hours: 4,
  });
  const [exportingWord, setExportingWord] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  // Auto-hide success message after 5 seconds and scroll to top
  useEffect(() => {
    if (successMessage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });

      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadData = async () => {
    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      setCurrentUser(user);

      // Get study plan via email-service API
      const response = await authenticatedFetch('/api/study-plans/get', {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
          study_plan_id: id,
        }),
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to load study plan');
      }

      setPlan(result.study_plan);

      // Task #348: hent fire-sone terskler fra DB
      try {
        const tRes = await authenticatedFetch('/api/validation-rules/thresholds');
        if (tRes.ok) {
          const td = await tRes.json();
          if (td?.thresholds) setThresholds(td.thresholds);
        }
      } catch (e) {
        console.warn('Could not load thresholds, using defaults:', e);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!currentUser) return;

    try {
      setProcessing(true);

      // Use overrideReason if provided, otherwise use default message
      const changeComment = overrideReason.trim() || 'Godkjent basert på KI-validering';

      const response = await authenticatedFetch('/api/study-plans/approve', {
        method: 'POST',
        body: JSON.stringify({
          admin_id: currentUser.id,
          study_plan_id: id,
          change_comment: changeComment,
        }),
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to approve study plan');
      }

      setSuccessMessage('Studieplan godkjent! Godkjenningslenke er generert.');
      setShowApproveModal(false);
      setOverrideReason('');

      // Reload plan data to show updated status
      await loadData();
    } catch (err: any) {
      setError(`Feil ved godkjenning: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    // TODO: Implement with email-service API
    toast.showInfo('Avvisningsfunksjonalitet er under utvikling. Bruk database-funksjoner direkte for nå.');
    setShowRejectModal(false);
  };

  const handleRequestChanges = async () => {
    // TODO: Implement with email-service API
    toast.showInfo('Funksjonalitet for å be om endringer er under utvikling. Bruk database-funksjoner direkte for nå.');
  };

  const handleExportWord = async () => {
    if (!plan) return;

    try {
      setExportingWord(true);
      await generateStudyPlanWord(plan);
    } catch (err: any) {
      setError(`Feil ved Word-eksport: ${err.message}`);
    } finally {
      setExportingWord(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="animate-spin" size={48} />
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card text-center py-12">
          <AlertCircle className="mx-auto mb-4 text-red-600" size={64} />
          <h2 className="text-xl font-bold mb-2">Feil</h2>
          <p className="text-gray-600 mb-4">{error || 'Studieplan ikke funnet'}</p>
          <Link to="/study-plans" className="btn btn-primary">
            Tilbake til oversikten
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <CheckCircle className="text-green-600" size={20} />
            <p className="text-green-800 font-medium">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <Link to="/study-plans" className="text-kf-primary hover:text-kf-secondary flex items-center gap-2 mb-4">
          <ArrowLeft size={20} />
          Tilbake til oversikten
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">{plan.title}</h1>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(plan.status)}`}>
                {formatStatus(plan.status)}
              </span>
              {plan.validation_score !== null && plan.validation_score !== undefined && (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  Number(plan.validation_score) >= thresholds.auto_approve_threshold ? 'bg-green-100 text-green-800'
                  : Number(plan.validation_score) >= thresholds.manual_review_threshold ? 'bg-yellow-100 text-kf-accent'
                  : 'bg-red-100 text-red-800'
                }`}>
                  Resultat: {Number(plan.validation_score).toFixed(1)}%
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Link
              to={`/study-plans/${plan.id}/questions`}
              className="btn btn-secondary flex items-center gap-2"
            >
              <BookOpen size={18} />
              Spørsmålsbank
            </Link>
            <Link
              to={`/study-plans/${plan.id}/licenses`}
              className="btn btn-secondary flex items-center gap-2"
            >
              <Shield size={18} />
              Lisenser
            </Link>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {(plan.status === 'submitted' || plan.status === 'under_review') && currentUser && currentUser.role !== 'member' && (
        <div className="card mb-6 bg-kf-light">
          <h3 className="text-lg font-semibold mb-4">Godkjenningshandlinger</h3>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => setShowApproveModal(true)}
              className="btn btn-primary flex items-center gap-2"
              disabled={processing}
            >
              <CheckCircle size={18} />
              Godkjenn
            </button>

            <button
              onClick={() => setShowRejectModal(true)}
              className="btn btn-danger flex items-center gap-2"
              disabled={processing}
            >
              <XCircle size={18} />
              Avvis
            </button>

            {plan.status === 'submitted' && (
              <button
                onClick={handleRequestChanges}
                className="btn btn-secondary flex items-center gap-2"
                disabled={processing}
              >
                <Clock size={18} />
                Sett under vurdering
              </button>
            )}
          </div>
        </div>
      )}

      {/* Rejection Reason Display */}
      {plan.status === 'rejected' && plan.rejection_reason && (
        <div className="card mb-6 bg-red-50 border-red-200">
          <h3 className="text-lg font-semibold mb-2 text-red-900">Avvisningsgrunn</h3>
          <p className="text-red-800">{plan.rejection_reason}</p>
        </div>
      )}

      {/* Approval Info */}
      {plan.status === 'approved' && plan.approved_at && (
        <div className="card mb-6 bg-green-50 border-green-200">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle className="text-green-600" size={24} />
            <h3 className="text-lg font-semibold text-green-900">Godkjent studieplan</h3>
          </div>
          <div className="text-sm text-green-800">
            <p>Godkjent: {formatDateTime(plan.approved_at)}</p>
            {(plan as any).approver_name && <p>Av: {(plan as any).approver_name}</p>}
            {(plan as any).study_association_name && <p>Studieforbund: {(plan as any).study_association_name}</p>}
            {plan.approval_link && (
              <Link
                to={`/verify/${plan.approval_link}`}
                className="text-green-700 underline hover:text-green-900 mt-2 inline-block"
              >
                Se offentlig verifisering →
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Validation Results */}
      {plan.validation_feedback && (
        <div className="card mb-6">
          <h3 className="text-xl font-semibold mb-4">Valideringsresultat</h3>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-700">Totalscore</span>
              <span className={`text-2xl font-bold ${
                (Number(plan.validation_score) || 0) >= thresholds.auto_approve_threshold ? 'text-green-600'
                : (Number(plan.validation_score) || 0) >= thresholds.manual_review_threshold ? 'text-kf-warning'
                : 'text-red-600'
              }`}>
                {Number(plan.validation_score || 0).toFixed(1)}%
              </span>
            </div>
            <p className="text-gray-600">{plan.validation_feedback.summary}</p>
          </div>

          <div className="space-y-3">
            <FeedbackItem
              label="Læringsutbytte"
              score={plan.validation_feedback.learning_outcomes_quality?.score}
              feedback={plan.validation_feedback.learning_outcomes_quality?.feedback}
              maxScore={25}
            />
            <FeedbackItem
              label="Omfang og arbeidsmengde"
              score={plan.validation_feedback.workload_alignment?.score || plan.validation_feedback.ects_alignment?.score}
              feedback={plan.validation_feedback.workload_alignment?.feedback || plan.validation_feedback.ects_alignment?.feedback}
              maxScore={10}
            />
            <FeedbackItem
              label="Vurdering og sammenheng"
              score={plan.validation_feedback.constructive_alignment?.score || plan.validation_feedback.assessment_quality?.score}
              feedback={plan.validation_feedback.constructive_alignment?.feedback || plan.validation_feedback.assessment_quality?.feedback}
              maxScore={15}
            />
            <FeedbackItem
              label="Struktur og fullstendighet"
              score={plan.validation_feedback.structure_quality?.score}
              feedback={plan.validation_feedback.structure_quality?.feedback}
              maxScore={10}
            />
            <FeedbackItem
              label="Metadata og compliance"
              score={plan.validation_feedback.metadata_compliance?.score}
              feedback={plan.validation_feedback.metadata_compliance?.feedback}
              maxScore={10}
            />
            {plan.validation_feedback.microcredential_fitness && (
              <FeedbackItem
                label="Egnethet som micro-credential"
                score={plan.validation_feedback.microcredential_fitness?.score}
                feedback={plan.validation_feedback.microcredential_fitness?.feedback}
                maxScore={10}
              />
            )}
          </div>

          {/* Quick approve button — kun for grønn sone (score >= auto_approve) */}
          {currentUser && currentUser.role !== 'member' &&
           (plan.status === 'submitted' || plan.status === 'under_review') &&
           (Number(plan.validation_score) || 0) >= thresholds.auto_approve_threshold && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-green-900">Godkjenning anbefalt</p>
                  <p className="text-sm text-green-700">Studieplanen oppfyller kvalitetskravene (score: {Number(plan.validation_score).toFixed(1)}%)</p>
                </div>
                <button
                  onClick={() => setShowApproveModal(true)}
                  className="btn btn-primary flex items-center gap-2"
                  disabled={processing}
                >
                  <CheckCircle size={18} />
                  Godkjenn nå
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Study Plan Details */}
      <div className="space-y-6">
        {/* Meta Information */}
        <div className="card">
          <h3 className="text-xl font-semibold mb-4">Informasjon</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem icon={<User />} label="Opprettet av" value={(plan as any).uploader_name || 'Ukjent'} />
            <InfoItem icon={<Calendar />} label="Opprettet" value={formatDateTime(plan.created_at)} />
            <InfoItem icon={<FileText />} label="Kompetansepoeng" value={`${plan.ects_credits} Kompetansepoeng (ECTS)`} />
            <InfoItem icon={<Clock />} label="Estimerte timer" value={`${plan.estimated_hours} timer`} />
            <InfoItem icon={<Target />} label="EQF-nivå" value={`Nivå ${plan.metadata.eqf_level || 'N/A'}`} />
            <InfoItem
              icon={<BookOpen />}
              label="Studieforbund"
              value={(plan as any).study_association_name || 'Ukjent'}
            />
          </div>
        </div>

        {/* Description */}
        <div className="card">
          <h3 className="text-xl font-semibold mb-4">Beskrivelse</h3>
          <p className="text-gray-700 whitespace-pre-wrap">{plan.description}</p>
        </div>

        {/* Learning Outcomes */}
        <div className="card">
          <h3 className="text-xl font-semibold mb-4">Læringsutbytte</h3>

          {plan.learning_outcomes.knowledge && plan.learning_outcomes.knowledge.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-900 mb-2">Kunnskaper</h4>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                {plan.learning_outcomes.knowledge.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {plan.learning_outcomes.skills && plan.learning_outcomes.skills.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-900 mb-2">Ferdigheter</h4>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                {plan.learning_outcomes.skills.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {plan.learning_outcomes.competence && plan.learning_outcomes.competence.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-900 mb-2">Kompetanse</h4>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                {plan.learning_outcomes.competence.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Content Modules */}
        {plan.content_modules && plan.content_modules.length > 0 && (
          <div className="card">
            <h3 className="text-xl font-semibold mb-4">Innholdsmoduler</h3>
            <ul className="space-y-3 text-gray-700">
              {plan.content_modules.map((module: any, index: number) => {
                // v6.4+: strukturerte modul-objekter. Bakoverkompat til streng.
                if (typeof module === 'string') {
                  return <li key={index} className="list-disc list-inside">{module}</li>;
                }
                const title = module?.title || `Modul ${index + 1}`;
                const description = module?.description;
                const hours = module?.estimated_hours;
                return (
                  <li key={index} className="pl-4 border-l-4 border-kf-primary/30 py-1">
                    <div className="font-semibold text-gray-900">{title}</div>
                    {description && (
                      <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{description}</p>
                    )}
                    {typeof hours === 'number' && hours > 0 && (
                      <p className="text-xs text-gray-500 mt-1">Estimert tid: {hours} timer</p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Assessment Criteria */}
        <div className="card">
          <h3 className="text-xl font-semibold mb-4">Vurderingskriterier</h3>
          <p className="text-gray-700 whitespace-pre-wrap">{plan.assessment_criteria}</p>
        </div>

        {/* Assessment Methods */}
        {plan.metadata.assessment_methods && plan.metadata.assessment_methods.length > 0 && (
          <div className="card">
            <h3 className="text-xl font-semibold mb-4">Vurderingsmetoder</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              {plan.metadata.assessment_methods.map((method, index) => (
                <li key={index}>{method}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Export to Word button - Only show for non-approved plans */}
        {plan.status !== 'approved' && (
          <div className="card bg-kf-blue-50 border-kf-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-kf-blue-900 mb-1">Eksporter til Word-dokument</h3>
                <p className="text-sm text-kf-blue-700">
                  Last ned denne studieplanen som et formatert Word-dokument for redigering eller deling.
                </p>
              </div>
              <button
                onClick={handleExportWord}
                disabled={exportingWord}
                className="btn btn-primary flex items-center gap-2 ml-4"
              >
                {exportingWord ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Eksporterer...
                  </>
                ) : (
                  <>
                    <Download size={18} />
                    Eksporter til Word
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Version History - Only show if plan has been approved at least once */}
        {plan.status === 'approved' && currentUser && (
          <VersionHistory
            studyPlanId={plan.id}
            userId={currentUser.id}
            onVersionDeactivated={loadData}
          />
        )}

        {/* Version Comparison - Only show if plan has been approved */}
        {plan.status === 'approved' && currentUser && (
          <VersionCompare
            studyPlanId={plan.id}
            userId={currentUser.id}
          />
        )}
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Godkjenn studieplan</h3>

            {/* Task #348: begrunnelse-krav basert på hvilken sone score ligger i */}
            {plan.validation_score !== null && plan.validation_score < thresholds.auto_approve_threshold ? (
              <>
                <div className="bg-kf-accent-20 border border-kf-accent-40 rounded p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="text-kf-warning flex-shrink-0 mt-0.5" size={20} />
                    <div className="text-sm text-kf-accent">
                      <p className="font-medium mb-1">
                        {plan.validation_score < thresholds.manual_review_threshold
                          ? 'Advarsel: Score under kvalitetsterskelen (overprøving)'
                          : 'Godkjenning krever begrunnelse'}
                      </p>
                      <p>
                        Denne studieplanen har en AI-score på <strong>{Number(plan.validation_score).toFixed(1)}%</strong>.
                        {plan.validation_score < thresholds.manual_review_threshold
                          ? ` Score er under kvalitetsterskelen på ${thresholds.manual_review_threshold}%.`
                          : ` Score er mellom ${thresholds.manual_review_threshold}% og ${thresholds.auto_approve_threshold}% og krever begrunnelse for godkjenning.`}
                      </p>
                      <p className="mt-2">
                        {plan.validation_score < thresholds.manual_review_threshold
                          ? 'Overprøving krever streng begrunnelse. Begrunnelsen vil bli lagret som audit-trail.'
                          : 'Skriv en grundig begrunnelse for godkjenningen. Denne lagres i systemet.'}
                      </p>
                    </div>
                  </div>
                </div>

                <label className="label text-sm font-medium mb-2">Begrunnelse for godkjenning (påkrevd) *</label>
                <textarea
                  className="input mb-4"
                  rows={4}
                  placeholder="Eksempel: Studieplanen oppfyller minimumskrav for voksenopplæring, og manglene vil rettes opp i revisjon..."
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                />
              </>
            ) : (
              <>
                <p className="text-gray-600 mb-4">
                  Er du sikker på at du vil godkjenne denne studieplanen? Det vil genereres en unik
                  verifiseringslenke som kan deles offentlig.
                </p>

                <label className="label text-sm font-medium mb-2">Ytterligere merknader til godkjenningen:</label>
                <textarea
                  className="input mb-4"
                  rows={3}
                  placeholder="Valgfritt: Legg til merknader til godkjenningen..."
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                />
              </>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleApprove}
                disabled={processing || (plan.validation_score !== null && plan.validation_score < thresholds.auto_approve_threshold && !overrideReason.trim())}
                className="btn btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {processing ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <CheckCircle size={18} />
                )}
                Godkjenn
              </button>
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setOverrideReason('');
                }}
                disabled={processing}
                className="btn btn-secondary flex-1"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Avvis studieplan</h3>
            <p className="text-gray-600 mb-4">
              Vennligst oppgi en grunn for avvisning. Dette vil være synlig for opplasteren.
            </p>
            <textarea
              className="input mb-4"
              rows={4}
              placeholder="Årsak til avvisning..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
            <div className="flex gap-3">
              <button
                onClick={handleReject}
                disabled={processing || !rejectionReason.trim()}
                className="btn btn-danger flex-1 flex items-center justify-center gap-2"
              >
                {processing ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <XCircle size={18} />
                )}
                Avvis
              </button>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                disabled={processing}
                className="btn btn-secondary flex-1"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface InfoItemProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function InfoItem({ icon, label, value }: InfoItemProps) {
  return (
    <div className="flex items-center gap-3">
      <div className="text-kf-primary">{icon}</div>
      <div>
        <p className="text-sm text-gray-600">{label}</p>
        <p className="font-medium text-gray-900">{value}</p>
      </div>
    </div>
  );
}

interface FeedbackItemProps {
  label: string;
  score: number;
  feedback: string;
  maxScore?: number;
}

function FeedbackItem({ label, score, feedback, maxScore = 100 }: FeedbackItemProps) {
  // Handle undefined or null scores
  const safeScore = Number(score || 0);
  // Calculate percentage based on maxScore
  const percentage = maxScore > 0 ? (safeScore / maxScore) * 100 : 0;

  const getScoreColor = (pct: number) => {
    if (pct >= 70) return 'text-green-600';
    if (pct >= 50) return 'text-kf-warning';
    return 'text-red-600';
  };

  return (
    <div className="border-l-4 border-gray-200 pl-4">
      <div className="flex items-center justify-between mb-1">
        <span className="font-medium text-gray-900">{label}</span>
        <span className={`font-bold ${getScoreColor(percentage)}`}>
          {safeScore}/{maxScore} ({percentage.toFixed(0)}%)
        </span>
      </div>
      {feedback && <p className="text-sm text-gray-600">{feedback}</p>}
    </div>
  );
}
